import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { money, formatDate } from "@/lib/format";
import { toast } from "sonner";

const STATUSES = ["submitted", "under_review", "more_information_required", "quote_sent", "revised_offer", "accepted_cash", "accepted_trade", "declined", "awaiting_shipment", "in_transit", "received", "inspection", "approved", "payment_due", "paid", "inventory_created", "closed"];

const inputCls = "border border-zinc-200 rounded-md px-2.5 py-1.5 text-sm focus:border-teal-600 outline-none";
const btnCls = "bg-teal-600 hover:bg-teal-700 text-ink text-xs px-3 py-1.5 font-heading font-semibold uppercase rounded-full transition-colors";

function QuoteForm({ sub, onDone }) {
  const [cash, setCash] = useState(sub.comparable_avg_sale ? Math.round(sub.comparable_avg_sale * 0.3) : "");
  const [trade, setTrade] = useState(sub.comparable_avg_sale ? Math.round(sub.comparable_avg_sale * 0.4) : "");
  const submit = async () => {
    try {
      await api.put(`/admin/seller-submissions/${sub.id}/quote`, { cash_offer: Number(cash), trade_credit: Number(trade) });
      toast.success("Quote sent");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <div className="flex gap-2 items-end" data-testid="quote-form">
      <input type="number" value={cash} onChange={(e) => setCash(e.target.value)} placeholder="Cash offer" className={`${inputCls} w-28`} data-testid="quote-cash-input" />
      <input type="number" value={trade} onChange={(e) => setTrade(e.target.value)} placeholder="Trade credit" className={`${inputCls} w-28`} data-testid="quote-trade-input" />
      <button onClick={submit} className={btnCls} data-testid="send-quote-button">Send Quote</button>
    </div>
  );
}

function InspectForm({ sub, onDone }) {
  const [score, setScore] = useState(8);
  const [notes, setNotes] = useState("");
  const decide = async (decision) => {
    try {
      await api.put(`/admin/seller-submissions/${sub.id}/inspect`, { final_condition_score: Number(score), inspection_notes: notes, decision });
      toast.success("Inspection recorded");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-end" data-testid="inspect-form">
      <input type="number" min={1} max={10} value={score} onChange={(e) => setScore(e.target.value)} className={`${inputCls} w-20`} data-testid="inspect-score-input" />
      <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Inspection notes" className={`${inputCls} w-48`} data-testid="inspect-notes-input" />
      <button onClick={() => decide("accept_original")} className={btnCls} data-testid="inspect-accept-button">Accept Original Offer</button>
      <button onClick={() => decide("reject")} className="border border-zinc-200 hover:border-zinc-400 text-xs px-3 py-1.5 font-heading font-semibold uppercase rounded-full text-zinc-700 transition-colors" data-testid="inspect-reject-button">Reject Item</button>
    </div>
  );
}

function ConvertForm({ sub, brands, onDone }) {
  const [brandSlug, setBrandSlug] = useState(brands[0]?.slug || "");
  const [sellingPrice, setSellingPrice] = useState("");
  const submit = async () => {
    try {
      await api.post(`/admin/seller-submissions/${sub.id}/convert-to-inventory`, { brand_slug: brandSlug, selling_price: Number(sellingPrice), model: sub.model, condition_score: sub.final_condition_score || 8 });
      toast.success("Converted to inventory");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };
  return (
    <div className="flex gap-2 items-end" data-testid="convert-form">
      <select value={brandSlug} onChange={(e) => setBrandSlug(e.target.value)} className={inputCls}>
        {brands.map((b) => <option key={b.slug} value={b.slug}>{b.name}</option>)}
      </select>
      <input type="number" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} placeholder="Selling price" className={`${inputCls} w-28`} data-testid="convert-price-input" />
      <button onClick={submit} className={btnCls} data-testid="convert-to-inventory-button">Convert To Inventory</button>
    </div>
  );
}

export default function AdminSellSubmissions() {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("");
  const [brands, setBrands] = useState([]);
  const [payments, setPayments] = useState([]);

  const load = () => {
    api.get("/admin/seller-submissions", { params: { status: filter || undefined } }).then((res) => setSubs(res.data));
    api.get("/admin/seller-payments", { params: { status: "payment_due" } }).then((res) => setPayments(res.data));
  };
  useEffect(() => { load(); }, [filter]);
  useEffect(() => { api.get("/admin/brands").then((res) => setBrands(res.data)); }, []);

  const setSimpleStatus = async (id, status) => {
    await api.put(`/admin/seller-submissions/${id}/status`, { status });
    load();
  };

  const markPaid = async (paymentId) => {
    await api.post(`/admin/seller-payments/${paymentId}/mark-paid`, { payment_reference: "manual" });
    toast.success("Payment marked as paid");
    load();
  };

  return (
    <div className="p-8" data-testid="admin-submissions-page">
      <h1 className="font-serif text-3xl mb-6 text-zinc-900">Sell to Lookers</h1>
      <select value={filter} onChange={(e) => setFilter(e.target.value)} className={`${inputCls} mb-6`} data-testid="admin-submissions-status-filter">
        <option value="">All statuses</option>
        {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
      </select>

      {payments.length > 0 && (
        <div className="mb-8 bg-white border border-zinc-200 rounded-xl p-4" data-testid="seller-payments-due">
          <h2 className="font-heading text-xs uppercase tracking-wide mb-3 text-zinc-500">Seller Payments Due</h2>
          {payments.map((p) => (
            <div key={p.id} className="flex justify-between items-center text-sm py-1 text-zinc-800">
              <span>{money(p.amount)} ({p.method}) — submission {p.submission_id.slice(0, 8)}</span>
              <button onClick={() => markPaid(p.id)} className="text-xs text-teal-700 hover:text-teal-800 font-heading font-semibold uppercase" data-testid={`mark-paid-${p.id}`}>Mark Paid</button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {subs.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`admin-submission-${s.submission_number}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <div className="font-heading text-sm uppercase tracking-wide text-zinc-900">{s.submission_number} — {s.brand} {s.model}</div>
                <div className="text-xs text-zinc-400">{s.customer_name} · {formatDate(s.created_at)} · {s.photos.length} photos</div>
              </div>
              <select value={s.status} onChange={(e) => setSimpleStatus(s.id, e.target.value)} className={inputCls} data-testid={`submission-status-${s.submission_number}`}>
                {STATUSES.map((st) => <option key={st} value={st}>{st.replace(/_/g, " ")}</option>)}
              </select>
            </div>
            <div className="text-xs text-zinc-400 mb-3">
              Customer condition: {s.condition_self} · {s.comparable_count > 0 ? `Comparable avg sale ${money(s.comparable_avg_sale)}` : "No comparable sales yet"}
            </div>
            {s.photos.length > 0 && (
              <div className="flex gap-2 mb-3">
                {s.photos.slice(0, 6).map((p, idx) => <img key={`${s.id}-${idx}`} src={`${process.env.REACT_APP_BACKEND_URL}${p}`} alt="" className="w-14 h-14 object-cover rounded-md border border-zinc-200" />)}
              </div>
            )}
            {["submitted", "under_review"].includes(s.status) && <QuoteForm sub={s} onDone={load} />}
            {["awaiting_shipment", "in_transit", "received"].includes(s.status) && (
              <button onClick={() => setSimpleStatus(s.id, "inspection")} className={btnCls} data-testid={`start-inspection-${s.submission_number}`}>Start Inspection</button>
            )}
            {s.status === "inspection" && <InspectForm sub={s} onDone={load} />}
            {["approved", "paid"].includes(s.status) && <ConvertForm sub={s} brands={brands} onDone={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}
